import ExcelJS from "exceljs";
import { quantityColumnValueOf } from "../../OverviewModal/OverviewGrid/quantityColumnValueOf";
import { addSectionHeader, styleColumnHeaderRow } from "../utils";
import { buildGroupCells } from "./buildGroupCells";
import type { BuildCountListWorkbook } from "./types";

// Same palette as the sibling export sheets (useExportHandlers)
const SECTION_HEADER_BG = "FFD6E4F7";
const SECTION_HEADER_FONT = "FF003A8C";

export const buildCountListWorkbook: BuildCountListWorkbook = ({
  quantityColumns,
  rows,
  headerLabels,
  groupValuesOf,
  includePrices,
  formatPriceValue,
}) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(headerLabels.sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  const priceColumns: Partial<ExcelJS.Column>[] = includePrices
    ? [
        { key: "materialPrice", width: 14 },
        { key: "hours", width: 12 },
        { key: "hourlyRate", width: 12 },
        { key: "unitPrice", width: 14 },
      ]
    : [];

  const quantityColumnKeys = quantityColumns.map(
    (quantityColumn) => quantityColumn.id,
  );
  const positionColumnKeys = [
    "positionNumber",
    "description",
    "boqQuantity",
    "countedTotal",
    ...quantityColumnKeys,
    "diagrams",
  ];

  sheet.columns = [
    { key: "positionNumber", width: 14 },
    { key: "description", width: 60 },
    { key: "detectedType", width: 34 },
    { key: "unit", width: 12 },
    ...priceColumns,
    { key: "boqQuantity", width: 12 },
    { key: "countedTotal", width: 12 },
    ...quantityColumnKeys.map((key) => ({ key, width: 14 })),
    { key: "diagrams", width: 12 },
  ];

  // ExcelJS drops keys without a column, so price values need no gating below
  const headerRow = sheet.addRow({
    ...headerLabels,
    ...Object.fromEntries(
      quantityColumns.map((quantityColumn) => [
        quantityColumn.id,
        quantityColumn.header,
      ]),
    ),
  });
  styleColumnHeaderRow(headerRow);

  for (const row of rows) {
    if (row.kind === "section") {
      addSectionHeader(
        sheet,
        `${row.sectionNumber} ${row.sectionName}`,
        SECTION_HEADER_BG,
        SECTION_HEADER_FONT,
      );
      continue;
    }

    // Blank in the grid is an empty cell in the sheet
    const quantityCells = Object.fromEntries(
      quantityColumns.map((quantityColumn) => {
        const value = quantityColumnValueOf({ row, quantityColumn });
        return [quantityColumn.id, value === "" ? undefined : value];
      }),
    );
    const positionCells = {
      ...(row.kind === "position"
        ? {
            positionNumber: row.positionNumber,
            description: row.description,
            // The extraction is unvalidated JSON, so an absent quantity leaves the cell empty
            boqQuantity: Number.isFinite(row.lvQuantity)
              ? row.lvQuantity
              : undefined,
          }
        : {}),
      countedTotal: row.countedTotal,
      ...quantityCells,
      diagrams: row.diagramsValue !== 0 ? row.diagramsValue : undefined,
    };

    // The position spans one sheet row per group; its own cells are merged down over them
    const groupRows = buildGroupCells({ row, groupValuesOf, formatPriceValue });
    const addedRows = groupRows.map((groupCells, index) => {
      const addedRow = sheet.addRow(
        index === 0 ? { ...positionCells, ...groupCells } : groupCells,
      );
      addedRow.alignment = { wrapText: true, vertical: "top" };
      return addedRow;
    });
    if (addedRows.length > 1) {
      const firstRowNumber = addedRows[0].number;
      const lastRowNumber = addedRows[addedRows.length - 1].number;
      for (const key of positionColumnKeys) {
        const columnNumber = sheet.getColumn(key).number;
        sheet.mergeCells(
          firstRowNumber,
          columnNumber,
          lastRowNumber,
          columnNumber,
        );
      }
    }
  }

  return workbook;
};
